import { useEffect, useState } from 'react';

const inputClass = 'mt-2 w-full border border-[#383142] bg-[#0f0d14] px-3 py-2.5 text-sm text-[#f2edf5] outline-none transition focus:border-[#b66ee3] focus:ring-2 focus:ring-[#8e4bb5]/20';
const labelClass = 'block text-[10px] font-bold uppercase tracking-[.14em] text-[#afa2b9]';

const clone = (value) => JSON.parse(JSON.stringify(value));

// Orden del mapa general. `ceu` es la Ciudad Universitaria (alias `cu`).
const LOCATION_ORDER = [
  { id: 'centro', hint: 'Catedral · Centro Histórico' },
  { id: 'ceu', hint: 'Ciudad Universitaria (alias cu)' },
  { id: 'tollocan', hint: 'Paseo Tollocan · Galerías' },
  { id: 'norte', hint: 'San Pablo Autopan y alrededores' },
  { id: 'parque', hint: 'Parque Estatal Sierra Morelos' },
];

export default function LocationGpsEditor({ resource, onSave, onReset, isSaving, error, notice }) {
  const [draft, setDraft] = useState(() => clone(resource.config));
  useEffect(() => setDraft(clone(resource.config)), [resource]);

  const update = (id, key, value) => setDraft((current) => ({
    ...current,
    locations: { ...current.locations, [id]: { ...current.locations[id], [key]: value } },
  }));
  const formattedUpdate = resource.meta.updatedAt
    ? new Date(resource.meta.updatedAt).toLocaleString('es-MX', { dateStyle: 'medium', timeStyle: 'short' })
    : 'Sin cambios guardados';

  return (
    <form onSubmit={(event) => { event.preventDefault(); onSave(draft); }} className="mx-auto max-w-[1480px] px-5 py-7 lg:px-8">
      <div className="flex flex-wrap items-end justify-between gap-3 border-b border-[#312a3a] pb-5">
        <div>
          <p className="font-mono text-[9px] uppercase tracking-[.2em] text-[#a887b9]">Módulo · Mapa</p>
          <h2 className="mt-1 font-display text-4xl uppercase text-[#f3edf6]">Ubicación GPS</h2>
          <p className="mt-2 max-w-xl text-xs leading-5 text-[#8f8298]">
            Coordenadas de Toluca de Lerdo y radio detectable en km por cada
            ubicación del mapa general. El navegador ubica al jugador en la zona
            más cercana dentro de su radio y le asigna ese punto del mapa con un
            pin 📍. Fuera de todos los radios no hay pin.
          </p>
        </div>
        <div className="text-right font-mono text-[9px] leading-5 text-[#84768d]">
          <p>{resource.meta.source === 'defaults' ? 'Usando valores predeterminados' : `Versión ${resource.meta.version}`}</p>
          <p>{formattedUpdate}</p>
        </div>
      </div>

      <div className="mt-7 grid gap-5 lg:grid-cols-2">
        {LOCATION_ORDER.map(({ id, hint }) => {
          const lat = Number(draft.locations[id]?.lat);
          const lon = Number(draft.locations[id]?.lon);
          const mapsUrl = Number.isFinite(lat) && Number.isFinite(lon)
            ? `https://www.google.com/maps/search/?api=1&query=${lat},${lon}`
            : null;
          return (
            <fieldset key={id} className="border border-[#312a3a] bg-[#121019]/80 p-5">
              <legend className="px-2 font-mono text-[10px] uppercase tracking-[.18em] text-[#d8b6ee]">
                {id === 'ceu' ? 'C.U. (ceu)' : draft.locations[id]?.label || id}
              </legend>
              <p className="text-[11px] text-[#84768d]">{hint}</p>
              <div className="mt-4 grid gap-4 sm:grid-cols-2">
                <label className={`${labelClass} sm:col-span-2`}>Etiqueta (1–48)
                  <input type="text" maxLength="48" value={draft.locations[id]?.label || ''} onChange={(event) => update(id, 'label', event.target.value)} className={inputClass} />
                </label>
                <label className={labelClass}>Latitud (−90 a 90)
                  <input type="number" min="-90" max="90" step="0.00001" value={draft.locations[id]?.lat ?? ''} onChange={(event) => update(id, 'lat', Number(event.target.value))} className={inputClass} />
                </label>
                <label className={labelClass}>Longitud (−180 a 180)
                  <input type="number" min="-180" max="180" step="0.00001" value={draft.locations[id]?.lon ?? ''} onChange={(event) => update(id, 'lon', Number(event.target.value))} className={inputClass} />
                </label>
                <div className="sm:col-span-2">
                  {mapsUrl ? (
                    <a href={mapsUrl} target="_blank" rel="noreferrer" className="inline-block border border-[#3a3344] px-3 py-2 text-[10px] font-bold uppercase tracking-[.14em] text-[#d6c9df] transition hover:border-[#d8b6ee] hover:text-white">Abrir en Google Maps ↗</a>
                  ) : (
                    <span className="text-[11px] text-[#84768d]">Escribe latitud y longitud para ver el pin.</span>
                  )}
                </div>
                <label className={`${labelClass} sm:col-span-2`}>Radio detectable en km (0.1–20)
                  <input type="number" min="0.1" max="20" step="0.1" value={draft.locations[id]?.radiusKm ?? ''} onChange={(event) => update(id, 'radiusKm', Number(event.target.value))} className={inputClass} />
                </label>
              </div>
            </fieldset>
          );
        })}
      </div>

      {(error || notice) && <div className={`mt-5 border-l-2 px-4 py-3 text-sm ${error ? 'border-[#ef7f8f] bg-[#2b1219] text-[#ffd8de]' : 'border-[#7dd6ae] bg-[#0d251d] text-[#c8f6df]'}`} role="status">{error || notice}</div>}
      <div className="mt-6 flex flex-col-reverse gap-3 border-t border-[#312a3a] pt-5 sm:flex-row sm:justify-between">
        <button type="button" onClick={onReset} disabled={isSaving} className="px-3 py-3 text-xs font-bold text-[#baacbf] underline decoration-[#65546f] underline-offset-4 disabled:opacity-40">Restaurar coordenadas de Toluca</button>
        <button type="submit" disabled={isSaving} className="border border-[#d8b6ee] bg-[#754196] px-8 py-3.5 text-xs font-black uppercase tracking-[.15em] text-white shadow-[4px_4px_0_#050407] transition active:translate-x-0.5 active:translate-y-0.5 disabled:opacity-45">{isSaving ? 'Guardando…' : 'Guardar cambios'}</button>
      </div>
    </form>
  );
}
